<script setup lang="ts">
import type { SearchUrlParams } from '#shared/utils/search'
import type { EnrichedSearchResult } from '~/utils/solr/search'
import { sanitizeSearchQuery } from '#shared/utils/search'
import { faMagnifyingGlass } from '@fortawesome/pro-duotone-svg-icons'
import { faXmark } from '@fortawesome/pro-light-svg-icons'
import { useRouteQuery } from '@vueuse/router'
import { useRouter as useVueRouter } from 'vue-router'
import { RESULT_PAGE_SEARCH_DEBOUNCE_MS, SEARCH_DEBOUNCE_MS } from '~/constants/search'

const props = withDefaults(defineProps<{
  placeholder?: string
  disabled?: boolean
  searchResults: EnrichedSearchResult[]
  contentTypeOptions: { value: string, label: string, disabled: boolean }[]
  totalResults: number
  isLoading: boolean
  resettable?: boolean
  toggleContentType: (type: string, checked: boolean, singleSelect?: boolean) => void
  selectedContentTypes: string[]
  resetContentType: () => void
  spellcheckSuggestion?: string | null
}>(), {
  resettable: true,
})

const KEY = 'sq'
const route = useRoute()
const router = useVueRouter()
const searchDebounceMs = computed(() =>
  route.name === 'search-index' ? RESULT_PAGE_SEARCH_DEBOUNCE_MS : SEARCH_DEBOUNCE_MS,
)

const { searchHistory } = useSearchHistory('global')

const searchTerm = useRouteQuery<NonNullable<SearchUrlParams[typeof KEY]>>(KEY, '')
const page = useRouteQuery<number>('page', 1)

const { beginDraft, commitTerm, submit, clear, activeTermCommit } = useSearchRouteCommit()

const searchGlobalInputForm = ref<HTMLFormElement | null>(null)
const inputValue = ref<string>(searchTerm.value)

const open = defineModel('open', { default: false })

const inputRef = useTemplateRef('input')
const previewResultRef = useTemplateRef('previewResult')
const historyRef = useTemplateRef('history')
const containerRef = useTemplateRef('container')

const isFocused = ref<boolean>(false)
const showReset = computed(() => props.resettable && inputValue.value.length > 0)

const { theme } = useTheme()


// Debounced sync: only writes to route if value is still current (guards against stale writes)
const debouncedSyncToRoute = useDebounceFn((val: string) => {
  if (val !== inputValue.value) {
    return
  }

  if (route.name === 'search-index') {
    commitTerm(val)
  }
  else {
    searchTerm.value = val
  }
}, searchDebounceMs)

function isPreservedTrailingSpaceEcho(term: string) {
  const commit = activeTermCommit.value
  return (
    commit?.term === term && commit.value.endsWith(' ') && commit.value.replace(/ +$/, '') === term
  )
}

// External query changes invalidate pending typed input; the active delayed commit keeps its draft.
watch(
  searchTerm,
  (val) => {
    if (!isPreservedTrailingSpaceEcho(val)) {
      inputValue.value = val
    }
  },
  { flush: 'sync' },
)

// Back/forward navigation also invalidates pending input when the query is unchanged
onScopeDispose(router.options.history.listen(() => {
  inputValue.value = searchTerm.value
}))

// Typing → debounced sync to route
watch(inputValue, (val) => {
  if (val !== searchTerm.value) {
    debouncedSyncToRoute(val)
  }
})

watch([open, inputRef], ([isOpen, input]) => {
  if (isOpen && input) {
    input.select()
  }
})

function handleBlur(event: FocusEvent) {
  if (containerRef.value?.contains(event.relatedTarget as Node)) {
    return
  }
  isFocused.value = false
}

function resetSearchInput() {
  if (props.resettable) {
    inputValue.value = ''
    if (route.name === 'search-index') {
      clear()
      return
    }
    searchTerm.value = ''
    page.value = 1
  }
}

function selectSuggestion(value: string) {
  inputValue.value = value
  searchTerm.value = value
  page.value = 1
}

function handleSearch(event: Event) {
  const data = new FormData(event.target as HTMLFormElement)
  const value = String(data.get(KEY))
  const term = sanitizeSearchQuery(value)

  if (route.name === 'search-index') {
    inputValue.value = term
    submit(term)
    return
  }

  if (!term) {
    return navigateTo({ name: 'search-index', query: {} })
  }

  return navigateTo({ name: 'search-index', query: { sq: term } })
}
</script>

<template>
  <div class="flex flex-col gap-m">
    <form ref="searchGlobalInputForm" class="relative flex justify-center items-start gap-s w-full" @submit.prevent="handleSearch($event)">
      <div ref="container" class="flex flex-col gap-2xs w-full relative">
        <label for="search-input" class="sr-only">Søk i hele Juridika</label>
        <input
          id="search-input"
          ref="input"
          v-model="inputValue"
          :name="KEY"
          :placeholder="placeholder"
          :disabled="disabled"
          type="text"
          autocomplete="off"
          class="grow w-full !bg-surface-elevated placeholder:!text-secondary"
          :class="[inputVariants.base({ error: false, isSearch: true }), showReset ? '!pr-[92px]' : '!pr-[68px]', 'nav:!pr-m']"
          @blur="handleBlur"
          @input="beginDraft"
          @focus="isFocused = true"
          @keydown.down.prevent="(previewResultRef ?? historyRef)?.focusFirst()"
        >
        <button
          v-if="showReset"
          type="button"
          aria-label="Tøm søkefeltet"
          class="absolute right-[66px] nav:right-5 top-1/2 -translate-y-1/2"
          @click="resetSearchInput"
        >
          <Icon :icon="faXmark" size="sm" />
        </button>
        <div v-if="route.name !== 'search-index'" class="preview-container">
          <SearchLoadingPreviewSkeletonItem v-if="isLoading && !searchResults.length" />
          <SearchPreviewResult
            v-if="searchResults.length && inputValue.length && !isLoading && isFocused"
            ref="previewResult"
            :preview-results="searchResults"
            :content-type-options="contentTypeOptions"
            :toggle-content-type="toggleContentType"
            :selected-content-types="selectedContentTypes"
            :reset-content-type="resetContentType"
            :total-results="totalResults"
            :search-term="inputValue"
            @select="open = false"
            @focus-input="inputRef?.focus()"
          />
        </div>
        <div v-if="isFocused && searchHistory.length && !inputValue.length" class="preview-container p-s gap-xs">
          <SearchHistory ref="history" :search-history="searchHistory" context="global" @focus-input="inputRef?.focus()" />
        </div>
        <template v-if="route.name !== 'search-index' && isFocused && !isLoading">
          <div v-if="!searchHistory.length && !inputValue.length" class="preview-container p-s gap-xs">
            <SearchTipsAndEmpty :with-illustration="false" />
          </div>
          <div v-if="inputValue.length > 3 && searchResults.length === 0" class="preview-container p-s gap-xs">
            <SearchTipsAndEmpty :search-term="inputValue" :with-illustration="false" :suggestion="spellcheckSuggestion" @select="selectSuggestion" />
          </div>
        </template>
      </div>
      <Button :to="route.name === 'search-index' ? undefined : { name: 'search-index', query: { [KEY]: inputValue } }" type="submit" variant="tertiary" :disabled="disabled" class="absolute right-2xs top-1/2 -translate-y-1/2 nav:static nav:translate-y-0 !bg-transparent !p-0" :aria-label="`Søk etter ${inputValue}`">
        <Shape variant="hexagon" :icon="faMagnifyingGlass" scale="0.8" class="w-12.5 nav:w-16 !text-icon-default" :class="theme === 'blurple' ? 'fill-blurple-200' : 'fill-blurple-100'" />
      </Button>
    </form>
  </div>
</template>

<style scoped lang='postcss'>
.preview-container {
  @apply flex flex-col bg-surface-elevated rounded-lg absolute inset-x-0 mt-3xs shadow-sm top-full z-50 animate-slide-down;
}
</style>
